import React from "react";
import moment from "moment";
import { Switch } from "antd";

import EditableCell from "@tailormed/common-client/component/inline-edit/EditableCell";
import DropdownEditor from "@tailormed/common-client/component/inline-edit/editors/DropdownEditor";
import TextEditor from "@tailormed/common-client/component/inline-edit/editors/TextEditor";
import DateEditor from "@tailormed/common-client/component/inline-edit/editors/DateEditor";
import SwitchEditor from "@tailormed/common-client/component/inline-edit/editors/SwitchEditor";
import InlineEmptyPlusButton from "@tailormed/common-client/component/inline-edit/InlineEmptyPlusButton";

import { isEditablePatientColumn } from "./inlineEditConfig";
import { selectedViews } from "../../../constant/table";
import AssigneesInlineEditor from "./AssigneesInlineEditor";
import PatientFacilityInlineEditor from "./PatientFacilityInlineEditor";
import PatientPhysicianInlineEditor from "./PatientPhysicianInlineEditor";
import InlineEditEmptyPlaceholder from "./InlineEditEmptyPlaceholder";
import {
  AssigneeColumn,
  FacilityColumn,
  PatientDOBColumn,
  PatientPhoneNumberColumn,
  PatientSSNColumn
} from "../CommonColumns";
import { DeceasedLable } from "../../DeceasedLable";
import { PatientStatusCell, StatusPill } from "../../TableCells";
import { dateFormatter } from "../../../utils/date";
import { formatPhoneNumberWhileTyping, getPhoneTypeLabel } from "../../../utils/formaters";
import {
  getStatusTagStyle,
  getStatusTagHoverBackground,
  getStatusTagHoverBorder,
  PATIENT_STATUS_COLORS
} from "../../../util/statusTagStyles";
import { GRAY_200, GRAY_500 } from "../../../constant/colors";

export const buildPatientInlineEditCellRenderer = ({
  t,
  view,
  commitPatient,
  onEditStart,
  renderWithLink,
  patientStatusOptions = []
}) => {
  const isRecentPatientsView = view === selectedViews.recentPatients;
  const buildCell = (columnKey, record) => {
    switch (columnKey) {
      case "isDeceased": {
        // Bordered box on hover (like the other fields); SwitchEditor carries the same border so
        // the Switch stays put across hover → edit.
        const hoverToggleWrapper = (checked) => (
          <div
            className="tm-inline-edit-switch-frame"
            style={{
              display: "inline-flex",
              alignItems: "center",
              pointerEvents: "none",
              padding: "8px",
              border: `1px solid ${GRAY_200}`,
              borderRadius: 8
            }}
          >
            <Switch checked={checked} />
          </div>
        );
        return {
          editor: <SwitchEditor />,
          valueForEdit: Boolean(record.isDeceased),
          // `null` (not "") so EditableCell falls through to emptyHoverNode when not deceased.
          displayNode: record.isDeceased ? <DeceasedLable /> : null,
          displayHoverNode: record.isDeceased ? hoverToggleWrapper(true) : null,
          emptyHoverNode: hoverToggleWrapper(false),
          // Colored pill — skip the generic hover frame.
          disableHoverFrame: true
        };
      }
      case "phone_number": {
        const hasPhone = !!(record.phone_number || record.home_number);
        // The editor maps to the same field the column shows: primary (phone_number) with a
        // fallback to home_number (see useInlineEditPatient commit logic). Surface which type is
        // being edited so clearing the primary and revealing the home number isn't misleading.
        const phoneTypeLabel = getPhoneTypeLabel(record, t);
        return {
          editor: (
            <TextEditor
              placeholder={t("managePatientDetails.usPhonePattern")}
              prefix={<span style={{ color: GRAY_500 }}>{phoneTypeLabel}</span>}
              maxLength={14}
              style={{ minWidth: 125 }}
              allowClear
              format={(v) => formatPhoneNumberWhileTyping(v || "")}
              parse={(v) => (v || "").replace(/\D/g, "")}
              validate={(v) => {
                const digits = v || "";
                if (digits.length === 0) return true;
                if (!/^\d{10}$/.test(digits)) return t("managePatientDetails.please_enter_digits");
                return true;
              }}
            />
          ),
          valueForEdit: record.phone_number || record.home_number || "",
          displayNode: hasPhone ? <PatientPhoneNumberColumn record={record} /> : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-patient-phone-${record.patientId}`}
              ariaLabel={t("inline_edit.phone_placeholder")}
            />
          )
        };
      }
      case "dob": {
        // Display-only (MVP-30783); guard against invalid dob rendering.
        const hasValidDob = !!record.dob && moment(record.dob).isValid();
        return {
          displayNode: hasValidDob ? <PatientDOBColumn record={record} /> : null
        };
      }
      case "ssn":
        return {
          displayNode: record.social_security_number ? <PatientSSNColumn record={record} /> : null
        };
      case "assignee": {
        // Orphan-aware: only show the column when an assignee actually resolves to a name,
        // so orphan-id-only rows render the empty `+` instead of a tiny frame circle.
        const hasNamedAssignee = (record.assignees || []).some((a) => a.userName || a.assigneeName);
        return {
          editor: <AssigneesInlineEditor />,
          valueForEdit: (record.assignees || []).map((a) => String(a.assignee ?? a.id)),
          displayNode: hasNamedAssignee ? (
            <AssigneeColumn record={record} withoutResize={isRecentPatientsView} />
          ) : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-patient-assignee-${record.patientId}`}
              ariaLabel={t("inline_edit.assignee_placeholder")}
            />
          )
        };
      }
      case "facility":
        return {
          editor: <PatientFacilityInlineEditor placeholder={t("inline_edit.facility_placeholder")} />,
          valueForEdit: (record.facilities || []).map((f) => f.id),
          displayNode: record.facilities?.length ? <FacilityColumn record={record} /> : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-patient-facility-${record.patientId}`}
              ariaLabel={t("inline_edit.facility_placeholder")}
            />
          )
        };
      case "physician":
        return {
          editor: <PatientPhysicianInlineEditor placeholder={t("inline_edit.physician_placeholder")} />,
          valueForEdit: record.physicianId || null,
          displayNode: record?.physician_full_name || null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-patient-physician-${record.patientId}`}
              ariaLabel={t("inline_edit.physician_placeholder")}
            />
          )
        };
      case "patientStatus": {
        // A non-selectable system status (e.g. "archived") isn't in the options. Inject it as a
        // hidden option so the editor's selector renders it as a styled pill, without listing it
        // as a selectable choice.
        const isSelectableStatus = patientStatusOptions.some((opt) => opt.value === record.status);
        const statusOptions =
          record.status && !isSelectableStatus
            ? [...patientStatusOptions, { value: record.status, label: record.status, disabled: true, hidden: true }]
            : patientStatusOptions;
        return {
          editor: (
            <DropdownEditor
              options={statusOptions}
              listHeight={180}
              placeholder={t("inline_edit.status_placeholder")}
              showArrow={false}
              suffixIcon={null}
              optionRender={(opt) => (
                <span
                  className="tm-status-pill-option"
                  style={{
                    ...getStatusTagStyle(opt.label, PATIENT_STATUS_COLORS),
                    "--tm-status-hover-bg": getStatusTagHoverBackground(opt.label, PATIENT_STATUS_COLORS),
                    "--tm-status-hover-border": getStatusTagHoverBorder(opt.label, PATIENT_STATUS_COLORS)
                  }}
                >
                  {opt.label}
                </span>
              )}
            />
          ),
          valueForEdit: record.status,
          displayNode: !record.status ? (
            <InlineEditEmptyPlaceholder
              text={t("inline_edit.empty_placeholder")}
              testId={`inline-edit-empty-patient-status-${record.patientId}`}
            />
          ) : isSelectableStatus ? (
            <PatientStatusCell value={record.status} />
          ) : (
            // Non-selectable statuses (e.g. "archived") — styled gray pill, not raw text.
            <StatusPill label={record.status} colorMap={PATIENT_STATUS_COLORS} />
          )
        };
      }
      case "followupDate":
        return {
          editor: <DateEditor />,
          valueForEdit: record.followupDate ? moment(record.followupDate) : null,
          displayNode: record.followupDate ? dateFormatter(record.followupDate) : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-patient-followup-${record.patientId}`}
              ariaLabel="Follow-up date"
            />
          )
        };
      default:
        return null;
    }
  };

  return (columnKey, record) => {
    const cell = buildCell(columnKey, record);
    if (!cell || !isEditablePatientColumn(columnKey)) {
      return renderWithLink(record, () => cell?.displayNode ?? null);
    }
    // Wrap in `renderWithLink` (LinkableCell = `<Link>` → real `<a href>`) so right-click,
    // cmd-click, middle-click all work natively. EditableCell's inner `enterEdit` calls
    // stopPropagation/preventDefault so editable-affordance clicks don't trigger navigation.
    return renderWithLink(record, () => (
      <EditableCell
        value={cell.valueForEdit}
        displayNode={cell.displayNode}
        displayHoverNode={cell.displayHoverNode}
        emptyHoverNode={cell.emptyHoverNode}
        editor={cell.editor}
        onCommit={(newValue) => commitPatient(record, columnKey, newValue)}
        onEditStart={() => onEditStart && onEditStart(columnKey)}
        disableHoverFrame={cell.disableHoverFrame}
        testId={`inline-edit-patient-${columnKey}-${record.patientId}`}
      />
    ));
  };
};
